/**
 * Firestore Connection Manager
 * Handles connectivity issues and prevents assertion failures
 */

import { enableNetwork, disableNetwork } from 'firebase/firestore';
import { db } from '../firebaseConfig';

class FirestoreConnectionManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.isHandlingError = false;

    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);

    this.setupNetworkListeners();
  }

  setupNetworkListeners() {
    // Listen for network changes only. Avoid visibility-based toggling to reduce listener churn
    // that can lead to Firestore internal target state assertions under HMR/StrictMode.
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  handleOnline() {
    console.log('Network: Online');
    this.isOnline = true;
    this.reconnectAttempts = 0;
    this.enableFirestore();
  }

  handleOffline() {
    console.log('Network: Offline');
    this.isOnline = false;
    this.disableFirestore();
  }

  // Removed visibility-based network toggling to avoid race conditions with active listeners

  async enableFirestore() {
    if (this.isHandlingError) return;
    
    try {
      await enableNetwork(db);
      console.log('Firestore network enabled');
    } catch (error) {
      console.warn('Failed to enable Firestore network:', error);
      this.handleFirestoreError(error);
    }
  }

  async disableFirestore() {
    if (this.isHandlingError) return;
    
    try {
      await disableNetwork(db);
      console.log('Firestore network disabled');
    } catch (error) {
      console.warn('Failed to disable Firestore network:', error);
    }
  }

  async handleFirestoreError(error) {
    if (this.isHandlingError) return;
    
    this.isHandlingError = true;
    console.error('Firestore connection error:', error);
    
    try {
      // Try to recover by disabling and re-enabling network
      await this.disableFirestore();
      
      // Wait a moment before re-enabling
      setTimeout(async () => {
        if (this.isOnline && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Attempting Firestore reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          await this.enableFirestore();
        }
        this.isHandlingError = false;
      }, 2000);
      
    } catch (recoveryError) {
      console.error('Failed to recover Firestore connection:', recoveryError);
      this.isHandlingError = false;
    }
  }

  cleanup() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  // Reset connection state (useful for debugging)
  reset() {
    this.reconnectAttempts = 0;
    this.isHandlingError = false;
    
    if (this.isOnline) {
      this.enableFirestore();
    }
  }
}

// Create a robust singleton instance resilient to HMR/StrictMode re-imports
let connectionManager;
try {
  if (typeof window !== 'undefined') {
    if (window.__firestore_connection_manager__) {
      connectionManager = window.__firestore_connection_manager__;
    } else {
      connectionManager = new FirestoreConnectionManager();
      window.__firestore_connection_manager__ = connectionManager;
    }
  } else {
    connectionManager = new FirestoreConnectionManager();
  }
} catch {
  connectionManager = new FirestoreConnectionManager();
}

export default connectionManager;
